app.component("transcriptionSeatsView", {
    templateUrl: "app/src/js/components/paymentTransactions/transcription-seats.tmpl.html",
    controller: "transcriptionSeatsController",
    bindings: {
        /** Sends the caller back to the wallet tab, for the card and top-up actions that live there. */
        onOpenWallet: "&",

        /**
         * The practice wallet, already loaded by the parent tab. Read for the cards: the subscription
         * payload only reports whether a default one exists, and the backup is needed too.
         */
        wallet: "<",

        /**
         * A section the wallet tab has linked to, as { view: 'BUY' | 'ASSIGN' | 'USAGE' }. Held as an
         * object rather than a plain string so following the same link twice is still a change.
         */
        request: "<",
    },
});

/**
 * AI transcription seats, in one view: buying them, handing them out, and reporting what they used.
 *
 * All three run off a single load of the subscription and the usage payload, so the seat counts
 * beside a tier selector can never drift from the counts in the usage table, and moving between the
 * sections costs nothing.
 *
 * Two server rules shape most of what follows. Seat quantities may only be reduced to the number
 * actually assigned, and assignments are applied as one batch, because two users swapping tiers one
 * at a time would be rejected at the halfway state. Free trials sit outside all of it: they are
 * granted automatically, hold no seat, and can be neither assigned nor released.
 */
app.controller("transcriptionSeatsController", [
    "$scope",
    "$timeout",
    "$q",
    "$modal",
    "$templateRequest",
    "TranscriptionSeatsAPI",
    "Session",
    "USER_ROLE",
    function ($scope, $timeout, $q, $modal, $templateRequest, TranscriptionSeatsAPI, Session, USER_ROLE) {
        var ctrl = this;

        var CANCEL_SEATS_TEMPLATE = "app/src/js/components/paymentTransactions/transcription-seats-cancel.tmpl.html";

        $scope.Session = Session;
        $scope.API = TranscriptionSeatsAPI;
        $scope.TIER = TranscriptionSeatsAPI.TIER;

        /** Held as one stable array so ng-repeat is not handed a fresh literal on every digest. */
        $scope.TIERS = [TranscriptionSeatsAPI.TIER.STANDARD, TranscriptionSeatsAPI.TIER.PREMIUM];

        $scope.view = "BUY";

        $scope.isLoading = true;
        $scope.isLoadingUsage = false;
        $scope.isPreviewing = false;
        $scope.loadError = null;
        $scope.errorMessage = null;
        $scope.successMessage = null;
        $scope.searchText = "";

        /**
         * Whether a renewal is the change in flight. Held apart from the shared processing flag so the
         * renew button is the only control reporting progress while it runs.
         */
        $scope.isRenewing = false;

        $scope.plansResponse = null;
        $scope.plans = [];
        $scope.currency = null;
        $scope.subscription = null;
        $scope.preview = null;

        /** Queued reductions, rebuilt with the subscription. See setSubscription. */
        $scope.pendingChanges = [];

        $scope.usage = null;
        $scope.users = [];
        $scope.periods = [];
        $scope.selectedPeriod = null;

        /** The quantities being edited, keyed by tier. */
        $scope.seatDraft = {};

        /**
         * The tier each user is on, keyed by user ID. Null means no seat. Moved the moment a tier is
         * clicked so the row and the availability counts answer straight away, and put back if the
         * server refuses the change.
         */
        $scope.assignedTier = {};

        /** The user whose seat is in flight, so their row can show progress. */
        $scope.savingUserId = null;

        /** What the practice effectively owns today, so a quantity change can be detected. */
        var committedSeats = {};

        /** The period the tables are actually showing, so the picker can be put back if a load is refused. */
        var loadedPeriodStart = null;

        /** The row order for the period on screen, keyed by user ID, so it survives a seat change. */
        var sortRanks = {};

        /**
         * Held across a retry so a timeout on a purchase cannot charge the practice twice. Cleared
         * only once the server has accepted the change.
         */
        var requestId = null;

        /** The quantities requestId was issued for. A different change needs a key of its own. */
        var requestIdSeats = null;

        /**
         * The idempotency key for saving these quantities: the held one when this is a retry of the same
         * change, and a fresh one otherwise. Reusing a key with a different body is refused (or replayed)
         * as the earlier request, so the new change would never apply.
         */
        function requestIdFor(quantities) {
            var seats = JSON.stringify(quantities);
            if (!requestId || requestIdSeats !== seats) {
                requestId = TranscriptionSeatsAPI.newRequestId();
                requestIdSeats = seats;
            }
            return requestId;
        }

        var previewTimer = null;
        var previewSequence = 0;

        /**
         * A section the wallet tab asked for before the load had finished, held until it has: the view
         * is otherwise chosen from what the practice owns, which is not known yet.
         */
        var pendingView = null;

        // =================================================================
        // Lifecycle & Loading
        // =================================================================

        this.$onInit = function () {
            load();

            // Warms the template cache, so the cancel modal opens on the click rather than after a
            // fetch. Failure is silent: it only means the first open pays for the fetch as before.
            $templateRequest(CANCEL_SEATS_TEMPLATE).catch(angular.noop);
        };

        /** Follows a link from the wallet tab, which hands over a fresh request object each time. */
        this.$onChanges = function (changes) {
            if (!changes.request || !ctrl.request || !ctrl.request.view) {
                return;
            }

            if ($scope.isLoading) {
                pendingView = ctrl.request.view;
                return;
            }

            $scope.setView(ctrl.request.view);
        };

        function load() {
            $scope.isLoading = true;
            $scope.loadError = null;

            return $q
                .all({
                    plans: TranscriptionSeatsAPI.getSeatPlans(),
                    subscription: TranscriptionSeatsAPI.getSubscription(),
                    periods: TranscriptionSeatsAPI.getSeatUsagePeriods(),
                    usage: TranscriptionSeatsAPI.getSeatUsage(null),
                })
                .then(function (responses) {
                    $scope.plansResponse = responses.plans.data || {};
                    $scope.plans = $scope.plansResponse.plans || [];
                    $scope.currency = $scope.plansResponse.currency;
                    setSubscription(responses.subscription.data);
                    $scope.periods = responses.periods.data || [];
                    $scope.selectedPeriod =
                        $scope.periods.filter(function (period) {
                            return period.is_current_period;
                        })[0] || $scope.periods[0] || null;

                    applyUsage(responses.usage.data);
                    seedSeatDraft();

                    // A practice that already owns seats lands on the day-to-day job; one that owns
                    // none has nothing to assign yet. A section linked to from the wallet tab wins.
                    $scope.view = pendingView || ($scope.isSubscriptionLive() ? "ASSIGN" : "BUY");
                    pendingView = null;

                    requestPreview();
                })
                .catch(function (error) {
                    $scope.loadError = TranscriptionSeatsAPI.getErrorMessage(
                        error,
                        "Could not load AI transcription seats. Please try again shortly."
                    );
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isLoading = false;
                    });
                });
        }

        /**
         * Takes the server's usage payload as the single source of truth, so the table always ends up
         * rendering from the server rather than from the tier a click applied optimistically.
         */
        function applyUsage(usage) {
            var periodStart = usage ? usage.period_start_utc : null;

            // Every seat change re-reads the whole table, so ranks are held for as long as the same
            // period is on screen: a row must not jump out from under the cursor as it is clicked.
            if (periodStart !== loadedPeriodStart) {
                sortRanks = {};
            }

            $scope.usage = usage || null;
            $scope.users = (usage && usage.users) || [];
            loadedPeriodStart = periodStart;

            $scope.assignedTier = {};

            $scope.users.forEach(function (user) {
                var tier = user.seat_tier || null;

                $scope.assignedTier[user.user_id] = tier;

                // Trials sit between paid seats and unseated users, being the rows most likely to
                // need a seat.
                if (sortRanks[user.user_id] == null) {
                    sortRanks[user.user_id] =
                        user.is_active_in_practice === false ? 0 : TranscriptionSeatsAPI.isTrialTier(tier) ? 2 : tier ? 1 : 3;
                }

                user.sortRank = sortRanks[user.user_id];

                // Stamped on the row rather than read through a scope call, so the labels are worked
                // out once per load instead of on every digest the filtered table runs through.
                user.roleLabel = formatRoles(user.roles);
            });
        }

        /**
         * Seeds the quantity editor from a queued reduction where one exists, because that is the
         * practice's most recent intent, and from the owned count otherwise.
         */
        function seedSeatDraft() {
            $scope.seatDraft = {};
            committedSeats = {};

            $scope.plans.forEach(function (plan) {
                var allocation = TranscriptionSeatsAPI.getAllocation($scope.subscription, plan.tier);
                var effective = allocation.quantity_pending == null ? allocation.quantity_owned : allocation.quantity_pending;

                $scope.seatDraft[plan.tier] = effective;
                committedSeats[plan.tier] = effective;
            });
        }

        // =================================================================
        // Section switching
        // =================================================================

        $scope.setView = function (view) {
            if ($scope.view === view) {
                return;
            }

            $scope.view = view;
            $scope.errorMessage = null;
            $scope.successMessage = null;
        };

        $scope.openWallet = function () {
            if (ctrl.onOpenWallet) {
                ctrl.onOpenWallet();
            }
        };

        // =================================================================
        // Reading the current subscription
        // =================================================================

        $scope.getAllocation = function (tier) {
            return TranscriptionSeatsAPI.getAllocation($scope.subscription, tier);
        };

        $scope.isSubscriptionLive = function () {
            return TranscriptionSeatsAPI.isSubscriptionLive($scope.subscription);
        };

        $scope.hasSubscription = function () {
            return !!$scope.subscription && $scope.subscription.status !== "NONE";
        };

        $scope.isPaymentOverdue = function () {
            return !!$scope.subscription && ($scope.subscription.status === "PAST_DUE" || $scope.subscription.status === "UNPAID");
        };

        /**
         * Whether the practice is billed by invoice under an enterprise agreement. Seats are then
         * granted by CatTrax rather than purchased here, so the buy flow and card prompts hide.
         */
        $scope.isInvoiceBilled = function () {
            return !!$scope.subscription
                && $scope.subscription.billing_type === "INVOICE"
                && $scope.subscription.status !== "CANCELED";
        };

        /**
         * Whether a card exists that a seat invoice can actually be charged to.
         *
         * Only the primary counts. A seat subscription is created with the practice's default payment
         * method, and the server refuses to create one without it; the backup card is stored and
         * displayed but is never attached to the subscription, so Stripe would never reach for it.
         */
        $scope.hasPrimaryCard = function () {
            if (ctrl.wallet && ctrl.wallet.default_payment_method_id) {
                return true;
            }

            // The subscription's own echo, so the guard still holds before the wallet arrives.
            return !!$scope.subscription && $scope.subscription.has_default_payment_method === true;
        };

        $scope.hasBackupCard = function () {
            return !!(ctrl.wallet && ctrl.wallet.backup_payment_method_id);
        };

        $scope.hasAnyCard = function () {
            return $scope.hasPrimaryCard() || $scope.hasBackupCard();
        };

        /** A backup on its own cannot be billed, and is worth calling out as its own problem. */
        $scope.isBackupOnly = function () {
            return $scope.hasBackupCard() && !$scope.hasPrimaryCard();
        };

        /**
         * Whether the draft raises any tier above what is already paid for, which is the case that
         * bills immediately.
         *
         * Compared against the owned count rather than the draft's starting point, mirroring the
         * server: a practice that has queued a reduction from 5 to 2 and then edits back up to 4 is
         * still below the 5 it owns, so nothing is charged.
         */
        $scope.isIncrease = function () {
            return $scope.plans.some(function (plan) {
                return ($scope.seatDraft[plan.tier] || 0) > $scope.getAllocation(plan.tier).quantity_owned;
            });
        };

        $scope.getTotalOwned = function () {
            return $scope.getAllocation($scope.TIER.STANDARD).quantity_owned + $scope.getAllocation($scope.TIER.PREMIUM).quantity_owned;
        };

        $scope.getTotalAssigned = function () {
            return (
                $scope.getAllocation($scope.TIER.STANDARD).quantity_assigned +
                $scope.getAllocation($scope.TIER.PREMIUM).quantity_assigned
            );
        };

        /**
         * The only way the subscription is replaced, because the queued reductions have to be rebuilt
         * with it. They are held rather than derived in the template: a function handing ng-repeat a
         * freshly built array every digest never settles, which took the whole screen down as soon as
         * a reduction was confirmed and the first queued change appeared.
         */
        function setSubscription(subscription) {
            $scope.subscription = subscription || null;
            $scope.pendingChanges = [];

            $scope.plans.forEach(function (plan) {
                var allocation = TranscriptionSeatsAPI.getAllocation($scope.subscription, plan.tier);

                if (allocation.quantity_pending != null && allocation.quantity_pending !== allocation.quantity_owned) {
                    $scope.pendingChanges.push({
                        tier: plan.tier,
                        name: plan.name,
                        from: allocation.quantity_owned,
                        to: allocation.quantity_pending,
                    });
                }
            });
        }

        // =================================================================
        // Free trial
        // =================================================================

        /**
         * The trial is not a purchasable plan and never comes back from /plans, so who is on one is
         * read off the usage rows rather than assumed.
         */
        $scope.getTrialUsers = function () {
            return $scope.users.filter(function (user) {
                return TranscriptionSeatsAPI.isTrialTier(user.seat_tier);
            });
        };

        // =================================================================
        // Buying: editing quantities
        // =================================================================

        $scope.adjust = function (tier, delta) {
            $scope.setQuantity(tier, ($scope.seatDraft[tier] || 0) + delta);
        };

        $scope.setQuantity = function (tier, quantity) {
            var next = parseInt(quantity, 10);

            if (isNaN(next) || next < 0) {
                next = 0;
            }

            $scope.seatDraft[tier] = next;
            $scope.onSeatDraftChanged();
        };

        /**
         * Steps down only as far as the seats already handed out, so the control itself cannot
         * produce a change the server would reject.
         */
        $scope.getMinimumQuantity = function (tier) {
            if (!$scope.isSubscriptionLive()) {
                return 0;
            }

            return $scope.getAllocation(tier).quantity_assigned;
        };

        $scope.getTotalSeats = function () {
            return $scope.plans.reduce(function (total, plan) {
                return total + ($scope.seatDraft[plan.tier] || 0);
            }, 0);
        };

        $scope.hasSeatChanges = function () {
            return $scope.plans.some(function (plan) {
                return ($scope.seatDraft[plan.tier] || 0) !== (committedSeats[plan.tier] || 0);
            });
        };

        /**
         * Mirrors the server's own rules so a practice is told what is wrong while it is still
         * editing, rather than on save.
         */
        $scope.getValidationMessage = function () {
            var total = $scope.getTotalSeats();

            if (!$scope.isSubscriptionLive()) {
                if (total < 1) {
                    return "Choose at least one seat to start a subscription.";
                }
            } else {
                if (total < 1) {
                    return "A subscription must keep at least one seat. Cancel the subscription instead.";
                }

                var stranded = $scope.plans.filter(function (plan) {
                    return ($scope.seatDraft[plan.tier] || 0) < $scope.getAllocation(plan.tier).quantity_assigned;
                });

                if (stranded.length) {
                    return (
                        "Release " +
                        stranded
                            .map(function (plan) {
                                return plan.name.toLowerCase() + " seats";
                            })
                            .join(" and ") +
                        " from your team before reducing them."
                    );
                }
            }

            // Added seats are invoiced the moment they are confirmed, so a chargeable card has to be
            // on file first. The server refuses a new subscription outright without one, and on an
            // existing subscription Stripe declines the immediate invoice - both are worth catching
            // here rather than at the card. Pricing itself stays visible: only the commit is blocked.
            if ($scope.isIncrease() && !$scope.hasPrimaryCard()) {
                if ($scope.hasBackupCard()) {
                    return "Seat invoices are charged to the primary card, and this practice has only a backup. Set a primary card under Wallet & settings.";
                }

                return $scope.hasSubscription()
                    ? "Add a card under Wallet & settings before buying more seats."
                    : "Add a card under Wallet & settings before starting a subscription.";
            }

            return null;
        };

        $scope.canSaveSeats = function () {
            return (
                // A pending debounce counts as previewing: the preview on screen is for the previous
                // quantities, and the confirmation would quote its charge for the new ones.
                $scope.hasSeatChanges() && !$scope.getValidationMessage() && !$scope.isPreviewing && !previewTimer && !TranscriptionSeatsAPI.isProcessing
            );
        };

        // =================================================================
        // Buying: preview
        // =================================================================

        $scope.onSeatDraftChanged = function () {
            $scope.successMessage = null;
            $scope.errorMessage = null;

            if (previewTimer) {
                $timeout.cancel(previewTimer);
            }

            // Debounced because each preview is a live call through to Stripe. Cleared as it fires, so
            // canSaveSeats() can tell a preview still waiting to be asked for from one already answered.
            previewTimer = $timeout(function () {
                previewTimer = null;
                return requestPreview();
            }, 500);
        };

        function requestPreview() {
            // An invoice billed practice never prices seats through Stripe - the server rejects the
            // preview, and the buy flow is hidden for it anyway.
            if ($scope.isInvoiceBilled()) {
                $scope.preview = null;
                $scope.isPreviewing = false;
                return;
            }

            if ($scope.getTotalSeats() < 1) {
                $scope.preview = null;
                $scope.isPreviewing = false;
                return;
            }

            var sequence = ++previewSequence;
            $scope.isPreviewing = true;

            return TranscriptionSeatsAPI.previewSubscription(toSeatQuantities())
                .then(function (response) {
                    // A slower earlier preview must never overwrite a newer one.
                    if (sequence !== previewSequence) {
                        return;
                    }

                    $scope.preview = response.data || null;
                })
                .catch(function (error) {
                    if (sequence !== previewSequence) {
                        return;
                    }

                    $scope.preview = null;

                    // Stripe keeps no upcoming invoice for a subscription that is set to end, so it
                    // has nothing to price a change against and the preview fails for as long as the
                    // cancellation stands. Saying so beats "try again", which cannot help here.
                    if ($scope.subscription && $scope.subscription.cancel_at_period_end) {
                        $scope.errorMessage =
                            "Pricing cannot be shown while this subscription is set to end on " +
                            formatDate($scope.subscription.current_period_end_utc) +
                            ". Renew it to bring pricing back.";
                        return;
                    }

                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(error, "Could not price that change. Please try again.");
                })
                .finally(function () {
                    if (sequence === previewSequence) {
                        $timeout(function () {
                            $scope.isPreviewing = false;
                        });
                    }
                });
        }

        function toSeatQuantities() {
            return $scope.plans.map(function (plan) {
                return { tier: plan.tier, quantity: $scope.seatDraft[plan.tier] || 0 };
            });
        }

        $scope.getPreviewLine = function (tier) {
            if (!$scope.preview || !$scope.preview.lines) {
                return null;
            }

            return $scope.preview.lines.filter(function (line) {
                return line.tier === tier;
            })[0];
        };

        // =================================================================
        // Buying: saving
        // =================================================================

        $scope.saveSeats = function () {
            if (!$scope.canSaveSeats()) {
                return;
            }

            var modalScope = $scope.$new();

            // Only title and content are read off the modal config; anything else the shared template
            // renders has to be put on the scope. The name matters too: its close X calls
            // generalModal.hide(), so the instance has to be reachable under exactly that name.
            modalScope.negativeButton = "CANCEL";

            modalScope.onNegativeButtonClick = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
            };

            modalScope.onGeneralClick = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
                applySeatSave();
            };

            $scope.generalModal = $modal({
                scope: modalScope,
                templateUrl: "app/src/views/templates/modal.general.tpl.html",
                show: true,
                title: $scope.isSubscriptionLive() ? "Change seats" : "Start seat subscription",
                content: buildConfirmationText() + buildRenewalNote(),
                backdrop: true,
                keyboard: false,
            });
        };

        function buildConfirmationText() {
            var summary = $scope.plans
                .map(function (plan) {
                    return ($scope.seatDraft[plan.tier] || 0) + " " + plan.name.toLowerCase();
                })
                .join(" and ");

            if (!$scope.preview) {
                return "Set your subscription to " + summary + " seats?";
            }

            if ($scope.preview.charge_now_cents > 0) {
                return (
                    "Set your subscription to " +
                    summary +
                    " seats? Your card will be charged " +
                    formatMoney($scope.preview.charge_now_cents) +
                    " now, then " +
                    formatMoney($scope.preview.monthly_total_cents) +
                    " a month."
                );
            }

            if ($scope.preview.is_downgrade) {
                return (
                    "Reduce your subscription to " +
                    summary +
                    " seats? You keep the seats you have already paid for until " +
                    formatDate($scope.preview.next_billing_date_utc) +
                    ", when the new monthly total of " +
                    formatMoney($scope.preview.monthly_total_cents) +
                    " begins."
                );
            }

            return "Set your subscription to " + summary + " seats, at " + formatMoney($scope.preview.monthly_total_cents) + " a month?";
        }

        /**
         * Spells out that a change also lifts a queued cancellation, which is the whole reason the
         * practice is likely to be on this screen.
         */
        function buildRenewalNote() {
            if (!$scope.canRenew()) {
                return "";
            }

            return (
                " This also keeps the subscription running past " +
                formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null) +
                ", instead of ending then."
            );
        }

        function applySeatSave() {
            $scope.errorMessage = null;
            $scope.successMessage = null;
            TranscriptionSeatsAPI.isProcessing = true;

            // Read before the state is refreshed, because the wording describes the change just made.
            var wasDowngrade = !!$scope.preview && $scope.preview.is_downgrade;
            var wasEnding = $scope.canRenew();

            var quantities = toSeatQuantities();

            return TranscriptionSeatsAPI.saveSubscription(quantities, requestIdFor(quantities))
                .then(function (response) {
                    // The request succeeded, so a later change must not reuse this key.
                    requestId = null;

                    setSubscription(response.data);
                    seedSeatDraft();
                    requestPreview();

                    var message = wasDowngrade ? "Seat reduction scheduled." : "Seats updated.";

                    // A practice that came here to stop its subscription ending needs to be told that
                    // is what happened, not just that its seats changed.
                    if (wasEnding) {
                        message +=
                            " The subscription now renews on "
                            + formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null)
                            + ".";
                    }

                    $scope.successMessage = message;
                    $timeout(function () {
                        $scope.successMessage = null;
                    }, 5000);
                })
                .catch(function (error) {
                    // requestId is deliberately kept, so retrying this same change is safe.
                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(error, "Could not update your seats. Please try again.");
                })
                .finally(function () {
                    $timeout(function () {
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        }

        /**
         * Whether the subscription is set to end and can still be brought back. Once the period has
         * closed the server has nothing left to renew, and a fresh subscription has to be started.
         */
        $scope.canRenew = function () {
            return (
                !!$scope.subscription
                && $scope.subscription.cancel_at_period_end === true
                && $scope.isSubscriptionLive()
                && !$scope.isInvoiceBilled()
            );
        };

        $scope.renewSubscription = function () {
            if (!$scope.canRenew() || TranscriptionSeatsAPI.isProcessing) {
                return;
            }

            // A seat change already lifts the cancellation on the server, so an edited quantity is
            // renewed and applied in the one save. Renewing first and confirming afterwards would
            // charge the practice for seats it has to ask for twice.
            if ($scope.hasSeatChanges()) {
                $scope.saveSeats();
                return;
            }

            var modalScope = $scope.$new();

            modalScope.negativeButton = "NOT NOW";

            modalScope.onNegativeButtonClick = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
            };

            modalScope.onGeneralClick = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
                applyRenewal();
            };

            $scope.generalModal = $modal({
                scope: modalScope,
                templateUrl: "app/src/views/templates/modal.general.tpl.html",
                show: true,
                title: "Renew seat subscription",
                content:
                    "Your "
                    + $scope.getTotalOwned()
                    + " seats keep running past "
                    + formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null)
                    + ", billed monthly as before. Nothing is charged today. Renew the subscription?",
                backdrop: true,
                keyboard: false,
            });
        };

        /**
         * Clears the cancellation on its own, leaving the seat counts alone. The reply is the whole
         * subscription, so the status strip and the queued reductions are re-read from it, and
         * pricing is asked for again: the preview is refused for as long as a cancellation stands.
         *
         * The quantity editor is deliberately left as the practice has it. Reseeding it here threw
         * away a seat count that had been typed but not yet confirmed, and nothing a renewal returns
         * can move the owned counts it would be reseeded from anyway.
         */
        function applyRenewal() {
            $scope.errorMessage = null;
            $scope.successMessage = null;
            $scope.isRenewing = true;
            TranscriptionSeatsAPI.isProcessing = true;

            return TranscriptionSeatsAPI.resumeSubscription()
                .then(function (response) {
                    setSubscription(response.data);
                    requestPreview();

                    $scope.successMessage =
                        "Subscription renewed. Your seats now renew on "
                        + formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null)
                        + ".";
                    $timeout(function () {
                        $scope.successMessage = null;
                    }, 5000);
                })
                .catch(function (error) {
                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(
                        error,
                        "Could not renew the subscription. Please try again."
                    );
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isRenewing = false;
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        }

        // =================================================================
        // Cancelling
        // =================================================================

        /**
         * Opens the seat picker rather than cancelling outright.
         *
         * A practice reaching for "cancel" usually has one or two seats too many rather than no use
         * for transcription at all, and an all-or-nothing button gave it nowhere to say so. The
         * quantities come first here; ending the whole subscription is still one click away
         * underneath, behind its own confirmation.
         *
         * A seat someone is holding can go too. The server refuses to reduce a tier below the seats
         * it has assigned, so the modal asks who is giving theirs up and releases exactly those
         * people first - see applySeatCancellation.
         */
        $scope.cancelSubscription = function () {
            var modalScope = $scope.$new();

            var cancelPreviewTimer = null;
            var cancelPreviewSequence = 0;

            /** Increments per tick, so the earliest choice is the one dropped when a swap is needed. */
            var selectionSequence = 0;

            modalScope.rows = buildCancelRows();
            modalScope.preview = null;
            modalScope.isPreviewing = false;

            /**
             * Everything the template reads, worked out once per change and stamped onto the rows and
             * this object.
             *
             * Bindings were functions to begin with, and between them they walked both rows and called
             * each other several times per digest - every keystroke recomputed the lot, and the panel
             * visibly assembled itself as each one settled. A digest now only reads properties.
             */
            modalScope.state = {};

            /**
             * @param {boolean} syncSelection - whether to bring the release ticks back in line with
             *        the stepper. True when the stepper moved; false when the practice ticked a name
             *        itself, which must not be corrected under them.
             */
            function refresh(syncSelection) {
                var heldTotal = 0;
                var keptTotal = 0;

                modalScope.rows.forEach(function (row) {
                    row.cancelled = row.held - row.keep;
                    heldTotal += row.held;
                    keptTotal += row.keep;
                });

                // Taking every seat is the whole subscription, and then nobody is released at all: the
                // seats end with it. So this has to be settled before the surplus per tier.
                var isFull = heldTotal > 0 && keptTotal === 0;

                modalScope.rows.forEach(function (row) {
                    row.surplus = isFull ? 0 : Math.max(0, row.assigned - row.keep);
                });

                if (syncSelection) {
                    modalScope.rows.forEach(syncReleaseSelection);
                }

                var releaseTotal = 0;

                modalScope.rows.forEach(function (row) {
                    row.selectedCount = countSelected(row);
                    row.shortfall = row.surplus - row.selectedCount;
                    row.stranded = Math.max(0, row.surplus - row.candidates.length);
                    row.needsRelease = row.surplus > 0;
                    releaseTotal += row.selectedCount;
                });

                modalScope.state = {
                    heldTotal: heldTotal,
                    keptTotal: keptTotal,
                    cancelTotal: heldTotal - keptTotal,
                    isFull: isFull,
                    releaseTotal: releaseTotal,
                    validation: buildValidationMessage(),
                };
            }

            function countSelected(row) {
                return row.candidates.filter(function (candidate) {
                    return candidate.selected;
                }).length;
            }

            /**
             * Mirrors the server's rules while the practice is still choosing. A seat cannot come off a
             * tier until someone has been named to give it up, because the release has to go through
             * first for the reduction to be accepted at all.
             */
            function buildValidationMessage() {
                // A tier whose assigned count runs ahead of the users the usage table lists - a seat
                // record whose holder can no longer be resolved, most often - cannot have that holder
                // named here, and the server will not take the reduction until they are released.
                var stranded = modalScope.rows.filter(function (row) {
                    return row.stranded > 0;
                });

                if (stranded.length) {
                    return (
                        stranded
                            .map(function (row) {
                                return (
                                    row.stranded +
                                    " " +
                                    row.name.toLowerCase() +
                                    (row.stranded === 1 ? " seat is" : " seats are") +
                                    " held by someone this screen cannot name"
                                );
                            })
                            .join(", and ") +
                        ". Release them on the Assign seats tab, then reduce the count here."
                    );
                }

                var short = modalScope.rows.filter(function (row) {
                    return row.shortfall > 0;
                });

                if (!short.length) {
                    return null;
                }

                return (
                    "Choose " +
                    short
                        .map(function (row) {
                            return row.shortfall + " more " + row.name.toLowerCase() + (row.shortfall === 1 ? " user" : " users");
                        })
                        .join(" and ") +
                    " to release."
                );
            }

            modalScope.adjustKeep = function (row, delta) {
                modalScope.setKeep(row, row.keep + delta);
            };

            /**
             * The stepper counts seats kept, seeded at what the tier holds, so minus reduces the seat
             * count exactly as it does on the buy screen and works from the moment the modal opens.
             *
             * Clamped to nothing below zero and nothing above what is held; every other rule - a seat
             * somebody is using, or the last seat of the subscription - is a message or a confirmation
             * rather than a disabled button, because a dead stepper explains nothing.
             */
            modalScope.setKeep = function (row, value) {
                var next = parseInt(value, 10);

                if (isNaN(next) || next < 0) {
                    next = 0;
                }

                if (next > row.held) {
                    next = row.held;
                }

                row.keep = next;
                refresh(true);
                schedulePreview();
            };

            // -------------------------------------------------------------
            // Choosing who gives a seat up
            // -------------------------------------------------------------

            /**
             * Ticking a name while the quota is already full swaps out the earliest choice rather
             * than refusing the click: the practice is choosing between people, and being made to
             * untick someone first only reads as the control being broken.
             */
            modalScope.toggleRelease = function (row, candidate) {
                if (candidate.selected) {
                    candidate.selected = false;
                    refresh(false);
                    return;
                }

                candidate.selected = true;
                candidate.selectedAt = ++selectionSequence;

                var selected = row.candidates.filter(function (entry) {
                    return entry.selected;
                });

                while (selected.length > row.surplus) {
                    var earliest = selected.reduce(function (oldest, entry) {
                        return oldest === null || entry.selectedAt < oldest.selectedAt ? entry : oldest;
                    }, null);

                    earliest.selected = false;
                    selected = selected.filter(function (entry) {
                        return entry !== earliest;
                    });
                }

                refresh(false);
            };

            /**
             * Keeps the ticks in step with the stepper: filling up in candidate order as the count
             * rises, and giving back the most recent choice as it falls. Only the stepper runs this -
             * a name the practice unticks by hand is left alone and reported as a shortfall, rather
             * than being ticked straight back.
             */
            function syncReleaseSelection(row) {
                var selected = countSelected(row);

                row.candidates.forEach(function (candidate) {
                    if (candidate.selected || selected >= row.surplus) {
                        return;
                    }

                    candidate.selected = true;
                    candidate.selectedAt = ++selectionSequence;
                    selected++;
                });

                while (selected > row.surplus) {
                    var latest = row.candidates
                        .filter(function (candidate) {
                            return candidate.selected;
                        })
                        .reduce(function (newest, candidate) {
                            return newest === null || candidate.selectedAt > newest.selectedAt ? candidate : newest;
                        }, null);

                    latest.selected = false;
                    selected--;
                }
            }

            /** Everyone being released, across both tiers. */
            function toReleases() {
                return modalScope.rows.reduce(function (releases, row) {
                    return releases.concat(
                        row.candidates.filter(function (candidate) {
                            return candidate.selected;
                        })
                    );
                }, []);
            }

            // -------------------------------------------------------------
            // Pricing and confirming
            // -------------------------------------------------------------

            /** What the practice would be left holding, in the shape the save endpoint wants. */
            function toRemainingQuantities() {
                return $scope.plans.map(function (plan) {
                    var row = modalScope.rows.filter(function (candidate) {
                        return candidate.tier === plan.tier;
                    })[0];

                    return { tier: plan.tier, quantity: row ? row.keep : 0 };
                });
            }

            function schedulePreview() {
                if (cancelPreviewTimer) {
                    $timeout.cancel(cancelPreviewTimer);
                }

                // Debounced because each preview is a live call through to Stripe, but only just: the
                // figure it fills in is the one thing on the panel worth waiting for, and the previous
                // one stays on screen while it does.
                cancelPreviewTimer = $timeout(function () {
                    cancelPreviewTimer = null;
                    return runPreview();
                }, 250);
            }

            function runPreview() {
                // Stripe is never asked to price a subscription that is ending, and there is nothing to
                // price before a seat has actually come off.
                if (modalScope.state.cancelTotal < 1 || modalScope.state.isFull) {
                    cancelPreviewSequence++;
                    modalScope.preview = null;
                    modalScope.isPreviewing = false;
                    return;
                }

                var sequence = ++cancelPreviewSequence;
                modalScope.isPreviewing = true;

                return TranscriptionSeatsAPI.previewSubscription(toRemainingQuantities())
                    .then(function (response) {
                        if (sequence !== cancelPreviewSequence) {
                            return;
                        }

                        modalScope.preview = response.data || null;
                    })
                    .catch(function () {
                        if (sequence !== cancelPreviewSequence) {
                            return;
                        }

                        // The new monthly total is a nicety here - a reduction is never charged, so a
                        // refused preview drops the figure rather than blocking the cancellation.
                        modalScope.preview = null;
                    })
                    .finally(function () {
                        if (sequence === cancelPreviewSequence) {
                            $timeout(function () {
                                modalScope.isPreviewing = false;
                            });
                        }
                    });
            }

            /**
             * The one figure the summary shows, whatever state pricing is in. Deliberately never a
             * spinner in place of the row: swapping the line out and back moved everything below it
             * twice per step. A figure already on screen simply goes quiet until the next one lands.
             */
            modalScope.getFigure = function () {
                if (modalScope.state.isFull) {
                    return "Ends " + formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null);
                }

                if (modalScope.preview) {
                    return formatMoney(modalScope.preview.monthly_total_cents);
                }

                return modalScope.state.cancelTotal > 0 ? "Pricing..." : "-";
            };

            modalScope.canConfirm = function () {
                return (
                    modalScope.state.cancelTotal > 0 &&
                    !modalScope.state.validation &&
                    !modalScope.isPreviewing &&
                    !cancelPreviewTimer &&
                    !TranscriptionSeatsAPI.isProcessing
                );
            };

            modalScope.confirm = function () {
                if (!modalScope.canConfirm()) {
                    return;
                }

                // The server refuses a subscription with no seats left, so taking every one of them
                // is the cancellation proper - the same path as the button underneath, confirmation
                // and all, rather than a save that could only be rejected.
                if (modalScope.state.isFull) {
                    modalScope.cancelEverything();
                    return;
                }

                var quantities = toRemainingQuantities();
                var cancelled = modalScope.state.cancelTotal;
                var releases = toReleases();

                modalScope.close();
                applySeatCancellation(quantities, cancelled, releases);
            };

            /** Hands over to the all or nothing path, which asks for its own confirmation. */
            modalScope.cancelEverything = function () {
                modalScope.close();
                confirmFullCancellation();
            };

            modalScope.close = function () {
                if (cancelPreviewTimer) {
                    $timeout.cancel(cancelPreviewTimer);
                    cancelPreviewTimer = null;
                }

                // A preview still in flight must not land on a modal that has gone.
                cancelPreviewSequence++;

                if ($scope.cancelSeatsModal) {
                    $scope.cancelSeatsModal.hide();
                }
            };

            refresh(true);

            $scope.cancelSeatsModal = $modal({
                scope: modalScope,
                templateUrl: CANCEL_SEATS_TEMPLATE,
                show: true,
                backdrop: true,
                keyboard: false,
            });
        };

        /**
         * What each tier could give up, read from the queued quantity where one exists so the picker
         * starts from the practice's most recent intent, exactly as the buy editor does.
         *
         * An assigned seat counts as cancellable, because the modal can release its holder on the way
         * through. The candidates are gathered for the picker but deliberately do not cap the stepper:
         * the assigned counter and the usage rows can disagree, and letting that quietly re-impose the
         * old floor left a dead stepper with nothing on screen explaining it. A surplus the picker
         * cannot cover is a validation message instead.
         */
        function buildCancelRows() {
            return $scope.plans
                .map(function (plan) {
                    var allocation = $scope.getAllocation(plan.tier);
                    var held = allocation.quantity_pending == null ? allocation.quantity_owned : allocation.quantity_pending;
                    return {
                        tier: plan.tier,
                        name: plan.name,
                        held: held,
                        assigned: allocation.quantity_assigned,
                        candidates: buildReleaseCandidates(plan.tier),
                        keep: held,
                    };
                })
                .filter(function (row) {
                    return row.held > 0;
                });
        }

        /**
         * Who holds this tier's seats, worst placed first: a seat held by someone who has left the
         * practice is pure waste and should always be the first to go, and after that the seat that
         * has gone least used this period is the one a practice cutting back is most likely to mean.
         */
        function buildReleaseCandidates(tier) {
            return $scope.users
                .filter(function (user) {
                    return $scope.assignedTier[user.user_id] === tier;
                })
                .map(function (user) {
                    return {
                        userId: user.user_id,
                        name: $scope.getFullName(user),
                        email: user.email,
                        usedSeconds: (user.used_seconds || 0) + (user.overage_seconds || 0),
                        hasLeft: user.is_active_in_practice === false,
                        selected: false,
                        selectedAt: 0,
                    };
                })
                .sort(function (first, second) {
                    if (first.hasLeft !== second.hasLeft) {
                        return first.hasLeft ? -1 : 1;
                    }

                    if (first.usedSeconds !== second.usedSeconds) {
                        return first.usedSeconds - second.usedSeconds;
                    }

                    return first.name.localeCompare(second.name);
                });
        }

        /**
         * A partial cancellation is an ordinary quantity reduction as far as the server is concerned -
         * queued for the period boundary and never refunded - so it goes through the same save the buy
         * editor uses, and that editor is reseeded from the reply so the two cannot disagree.
         *
         * Where seats are being taken off people, the release has to land first: the server rejects a
         * tier reduced below the seats it has assigned. The two calls cannot be made atomic from here,
         * so the failure between them is reported for what it is rather than glossed over - the seats
         * are free to hand back out, and the reduction can simply be made again.
         */
        function applySeatCancellation(quantities, cancelledCount, releases) {
            $scope.errorMessage = null;
            $scope.successMessage = null;
            TranscriptionSeatsAPI.isProcessing = true;

            var released = false;

            var readyToReduce = !releases.length
                ? $q.when(null)
                : TranscriptionSeatsAPI.updateSeatAssignments(
                      releases.map(function (release) {
                          return { user_id: release.userId, tier: null };
                      })
                  ).then(function (response) {
                      released = true;
                      applyUsage(response.data);
                  });

            return readyToReduce
                .then(function () {
                    return TranscriptionSeatsAPI.saveSubscription(quantities, requestIdFor(quantities));
                })
                .then(function (response) {
                    // The request succeeded, so a later change must not reuse this key.
                    requestId = null;

                    setSubscription(response.data);
                    seedSeatDraft();
                    requestPreview();

                    $scope.successMessage =
                        cancelledCount +
                        (cancelledCount === 1 ? " seat is" : " seats are") +
                        " scheduled to end on " +
                        formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null) +
                        ". You keep them until then, and your remaining seats carry on as normal." +
                        buildReleaseNote(releases);

                    $timeout(function () {
                        $scope.successMessage = null;
                    }, 8000);
                })
                .catch(function (error) {
                    // requestId is deliberately kept, so retrying this same change is safe.
                    if (released) {
                        $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(
                            error,
                            "Those seats were released, but the seat count could not be reduced. Please try again - you are still paying for them."
                        );

                        // The releases did land, so the counts on screen have to move with them.
                        return TranscriptionSeatsAPI.getSubscription()
                            .then(function (response) {
                                setSubscription(response.data);
                                seedSeatDraft();
                            })
                            .catch(angular.noop);
                    }

                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(
                        error,
                        "Could not cancel those seats. Nothing has changed."
                    );
                })
                .finally(function () {
                    $timeout(function () {
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        }

        /** Names who lost a seat, because that part of the change happened immediately. */
        function buildReleaseNote(releases) {
            if (!releases.length) {
                return "";
            }

            var names = releases.map(function (release) {
                return release.name;
            });

            return (
                " " +
                (names.length === 1 ? names[0] + " no longer holds a seat" : names.join(", ") + " no longer hold a seat") +
                " and now transcribes against the practice token wallet."
            );
        }

        /**
         * Ending the subscription outright, kept behind its own confirmation because the seat picker
         * has already offered the smaller change and this one takes every seat with it.
         */
        function confirmFullCancellation() {
            var modalScope = $scope.$new();

            modalScope.negativeButton = "KEEP SEATS";

            modalScope.onNegativeButtonClick = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
            };

            modalScope.onGeneralClick = function () {
                if ($scope.generalModal) {
                    $scope.generalModal.hide();
                }
                applyCancellation();
            };

            $scope.generalModal = $modal({
                scope: modalScope,
                templateUrl: "app/src/views/templates/modal.general.tpl.html",
                show: true,
                title: "Cancel the whole subscription",
                content:
                    "All " +
                    $scope.getTotalOwned() +
                    " seats stay active until " +
                    formatDate($scope.subscription ? $scope.subscription.current_period_end_utc : null) +
                    ", then transcription falls back to your token wallet. Cancel the whole subscription?",
                backdrop: true,
                keyboard: false,
            });
        }

        function applyCancellation() {
            $scope.errorMessage = null;
            TranscriptionSeatsAPI.isProcessing = true;

            return TranscriptionSeatsAPI.cancelSubscription()
                .then(function () {
                    return TranscriptionSeatsAPI.getSubscription();
                })
                .then(function (response) {
                    setSubscription(response.data);
                    seedSeatDraft();
                    $scope.successMessage = "Subscription will end at the close of the current billing period.";
                })
                .catch(function (error) {
                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(
                        error,
                        "Could not cancel the subscription. Please try again."
                    );
                })
                .finally(function () {
                    $timeout(function () {
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        }

        // =================================================================
        // Seat availability
        // =================================================================

        /**
         * Counts what the rows are showing rather than the last subscription payload, so the remaining
         * count is already right while a change is still in flight.
         */
        $scope.getAssignedCount = function (tier) {
            return $scope.users.filter(function (user) {
                return $scope.assignedTier[user.user_id] === tier;
            }).length;
        };

        $scope.getAvailableCount = function (tier) {
            return $scope.getAllocation(tier).quantity_owned - $scope.getAssignedCount(tier);
        };

        /** Past periods are a report only - seats can only be moved in the period in progress. */
        $scope.isEditable = function () {
            return !!$scope.usage && $scope.usage.is_current_period !== false && $scope.isSubscriptionLive();
        };

        /** Whether the server currently has this user on their one free trial. */
        $scope.isTrialHolder = function (user) {
            return TranscriptionSeatsAPI.isTrialTier(user.seat_tier);
        };

        /**
         * Whether a row may be moved onto a tier. An orphaned seat can be released but never
         * re-assigned, because its holder is no longer a member of the practice.
         */
        $scope.canAssign = function (user, tier) {
            if (!$scope.isEditable()) {
                return false;
            }

            // Seats move one at a time: every change comes back with the whole table, so a second
            // click sent before the first has answered would only be re-rendered away.
            if (TranscriptionSeatsAPI.isProcessing) {
                return false;
            }

            // A trial is granted automatically and expires on its own, so the server refuses both
            // assigning one and releasing one. Its slot is shown filled but is never offered.
            if (TranscriptionSeatsAPI.isTrialTier(tier)) {
                return false;
            }

            if (!tier) {
                return !$scope.isTrialHolder(user);
            }

            if (user.is_active_in_practice === false) {
                return false;
            }

            if ($scope.assignedTier[user.user_id] === tier) {
                return true;
            }

            return $scope.getAvailableCount(tier) > 0;
        };

        // =================================================================
        // Assigning
        // =================================================================

        /**
         * Applies a tier the moment it is clicked. The row moves first so the click feels answered,
         * then the server's reply is what the table finally renders from.
         */
        $scope.setTier = function (user, tier) {
            var previous = $scope.assignedTier[user.user_id] || null;

            if (!$scope.canAssign(user, tier) || previous === (tier || null)) {
                return;
            }

            $scope.assignedTier[user.user_id] = tier;
            $scope.savingUserId = user.user_id;
            $scope.errorMessage = null;
            $scope.successMessage = null;

            applyAssignment(user, tier, previous);
        };

        /**
         * Sends the one change on its own. A refusal puts the row back where it was, so the screen
         * never shows a seat the practice does not actually hold.
         */
        function applyAssignment(user, tier, previous) {
            TranscriptionSeatsAPI.isProcessing = true;

            return TranscriptionSeatsAPI.updateSeatAssignments([{ user_id: user.user_id, tier: tier }])
                .then(
                    function (response) {
                        applyUsage(response.data);

                        $scope.successMessage = tier
                            ? $scope.getFullName(user) + " now has a " + $scope.getTierLabel(tier).toLowerCase() + " seat."
                            : $scope.getFullName(user) + " no longer has a seat.";

                        $timeout(function () {
                            $scope.successMessage = null;
                        }, 5000);

                        // The assigned counts have moved, so the header must be re-read too. Failing
                        // that only leaves the status strip stale, so it does not undo the row.
                        return TranscriptionSeatsAPI.getSubscription()
                            .then(function (subscriptionResponse) {
                                setSubscription(subscriptionResponse.data);
                            })
                            .catch(angular.noop);
                    },
                    function (error) {
                        $scope.assignedTier[user.user_id] = previous;
                        $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(
                            error,
                            "Could not update that seat. Please try again."
                        );
                    }
                )
                .finally(function () {
                    $timeout(function () {
                        $scope.savingUserId = null;
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        }

        // =================================================================
        // Usage: period selection
        // =================================================================

        /**
         * Called from ng-change, which fires after ng-model has already moved, so the period that is
         * actually loaded is tracked separately and the picker is put back whenever a load is refused.
         */
        $scope.selectPeriod = function (period) {
            if (!period || period.period_start_utc === loadedPeriodStart) {
                return;
            }

            $scope.errorMessage = null;
            $scope.isLoadingUsage = true;

            return TranscriptionSeatsAPI.getSeatUsage(period.is_current_period ? null : period.period_start_utc)
                .then(function (response) {
                    applyUsage(response.data);
                    $scope.selectedPeriod = findPeriod(loadedPeriodStart) || period;
                })
                .catch(function (error) {
                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(error, "Could not load usage for that period.");
                    $scope.selectedPeriod = findPeriod(loadedPeriodStart);
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isLoadingUsage = false;
                    });
                });
        };

        function findPeriod(periodStartUtc) {
            return (
                $scope.periods.filter(function (period) {
                    return period.period_start_utc === periodStartUtc;
                })[0] || null
            );
        }

        /**
         * The usage payload only carries an end date for the period in progress, so a past period
         * takes its boundary from the period list instead.
         */
        $scope.getPeriodEnd = function () {
            if ($scope.usage && $scope.usage.period_end_utc) {
                return $scope.usage.period_end_utc;
            }

            return $scope.selectedPeriod ? $scope.selectedPeriod.period_end_utc : null;
        };

        $scope.getPeriodLabel = function (period) {
            if (!period || !period.period_start_utc) {
                return "Current period";
            }

            var label = moment(period.period_start_utc).format("D MMM");

            if (period.period_end_utc) {
                label += " - " + moment(period.period_end_utc).format("D MMM YYYY");
            }

            return label + (period.is_current_period ? " (current)" : "");
        };

        // =================================================================
        // Presentation
        // =================================================================

        $scope.formatSeconds = TranscriptionSeatsAPI.formatSeconds;
        $scope.toMinutes = TranscriptionSeatsAPI.toMinutes;
        $scope.formatAllowanceHours = TranscriptionSeatsAPI.formatAllowanceHours;
        $scope.fromCents = TranscriptionSeatsAPI.fromCents;
        $scope.getTierLabel = TranscriptionSeatsAPI.getTierLabel;
        $scope.isTrialTier = TranscriptionSeatsAPI.isTrialTier;

        $scope.getUsagePercent = function (user) {
            if (!user.included_seconds) {
                return user.used_seconds > 0 ? 100 : 0;
            }

            return Math.min(100, Math.round(((user.used_seconds || 0) / user.included_seconds) * 100));
        };

        $scope.getTotalsPercent = function () {
            var totals = ($scope.usage || {}).totals;

            if (!totals || !totals.seconds_included) {
                return 0;
            }

            return Math.min(100, Math.round(((totals.seconds_used || 0) / totals.seconds_included) * 100));
        };

        /**
         * Turns the server's comma separated role codes into the labels the rest of the app uses, so a
         * row reads "Practice Admin" rather than "PRACTICE_ADMIN". A code the constant does not carry is
         * spelled out from the code itself rather than dropped, because an unlisted role still tells the
         * practice something about who a seat is going to.
         */
        function formatRoles(roles) {
            if (!roles) {
                return null;
            }

            return roles
                .split(",")
                .map(function (role) {
                    return role.trim();
                })
                .filter(function (role) {
                    return !!role;
                })
                .map(function (role) {
                    var known = USER_ROLE.find(function (option) {
                        return option.id === role;
                    });

                    return known ? known.label : titleCase(role);
                })
                .join(", ");
        }

        function titleCase(value) {
            return value
                .toLowerCase()
                .split("_")
                .map(function (word) {
                    return word.charAt(0).toUpperCase() + word.slice(1);
                })
                .join(" ");
        }

        $scope.getFullName = function (user) {
            return ((user.first_name || "") + " " + (user.last_name || "")).trim() || user.email || "User " + user.user_id;
        };

        /**
         * Narrows the table by name, email or role. Written out rather than left to Angular's default
         * filter, which would also match the internal sort key held on each row.
         */
        $scope.userFilter = function (user) {
            if (!$scope.searchText) {
                return true;
            }

            var needle = $scope.searchText.toLowerCase();

            return (
                $scope.getFullName(user).toLowerCase().indexOf(needle) !== -1 ||
                (user.email || "").toLowerCase().indexOf(needle) !== -1 ||
                (user.roleLabel || "").toLowerCase().indexOf(needle) !== -1
            );
        };

        /** Paid seats only - a trial costs the practice nothing and holds no seat. */
        $scope.getSeatedUserCount = function () {
            return $scope.users.filter(function (user) {
                return !!user.seat_tier && !TranscriptionSeatsAPI.isTrialTier(user.seat_tier);
            }).length;
        };

        $scope.getTrialUserCount = function () {
            return $scope.getTrialUsers().length;
        };

        /** Rows with usage but no seat: their transcription is being billed to the token wallet. */
        $scope.getWalletOnlyUserCount = function () {
            return $scope.users.filter(function (user) {
                return !user.seat_tier && (user.used_seconds > 0 || user.overage_seconds > 0);
            }).length;
        };

        function formatMoney(cents) {
            return ($scope.currency ? $scope.currency + " " : "") + "$" + TranscriptionSeatsAPI.fromCents(cents).toFixed(2);
        }

        function formatDate(dateUtc) {
            if (!dateUtc) {
                return "the end of the billing period";
            }

            return moment(dateUtc).format("D MMM YYYY");
        }

        $scope.formatMoney = formatMoney;

        $scope.$on("$destroy", function () {
            if (previewTimer) {
                $timeout.cancel(previewTimer);
            }
        });
    },
]);
